import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Check, ExternalLink, MapPin, RotateCcw, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { Location } from "@/types/weather";
import type { AdminWebcamsResponse, WebcamCandidate, WebcamReview } from "@shared/webcams";

type ReviewStatus = WebcamReview["status"];

async function requestJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, { ...init, credentials: "include" });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.message || body.error || `Request failed (${response.status})`);
  }
  return response.json() as Promise<T>;
}

function prettyDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Date unavailable" : date.toLocaleString();
}

export default function AdminWebcams() {
  const queryClient = useQueryClient();
  const [locationId, setLocationId] = useState("");
  const [candidateId, setCandidateId] = useState<number | null>(null);
  const [rank, setRank] = useState("0");
  const [confirmedSpotView, setConfirmedSpotView] = useState(false);
  const [notice, setNotice] = useState("");

  const locationsQuery = useQuery<Location[]>({
    queryKey: ["/api/locations/all"],
    queryFn: () => requestJson<Location[]>("/api/locations/all"),
    staleTime: 5 * 60_000,
  });
  const locations = useMemo(() => locationsQuery.data ?? [], [locationsQuery.data]);
  const selectedLocationId = Number(locationId);
  const webcamsQuery = useQuery<AdminWebcamsResponse>({
    queryKey: ["/api/admin/locations", selectedLocationId, "webcams"],
    queryFn: () => requestJson<AdminWebcamsResponse>(`/api/admin/locations/${selectedLocationId}/webcams`),
    enabled: Number.isInteger(selectedLocationId) && selectedLocationId > 0,
    retry: 1,
  });

  const candidates = webcamsQuery.data?.webcams ?? [];
  const reviews = webcamsQuery.data?.reviews ?? [];
  const selectedCandidate = candidates.find((camera) => camera.id === candidateId) ?? null;
  const selectedReview = reviews.find((review) => review.cameraId === candidateId) ?? null;
  const reviewMutation = useMutation({
    mutationFn: async (input: { cameraId: number; status: ReviewStatus; sortOrder: number }) =>
      requestJson<unknown>(
        `/api/admin/locations/${selectedLocationId}/webcams/${input.cameraId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            status: input.status,
            sortOrder: input.sortOrder,
            ...(input.status === "approved" ? { confirmsSpotView: true } : {}),
            ...(input.status === "approved" ? { reviewToken: selectedCandidate?.reviewToken } : {}),
          }),
        },
      ),
    onSuccess: async (_, variables) => {
      setNotice(variables.status === "approved" ? "Camera approved for this spot." : "Camera excluded.");
      setConfirmedSpotView(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["/api/admin/locations", selectedLocationId, "webcams"] }),
        queryClient.invalidateQueries({ queryKey: ["/api/locations", selectedLocationId, "webcams"] }),
      ]);
    },
    onError: (error: Error) => setNotice(error.message),
  });
  const resetMutation = useMutation({
    mutationFn: (cameraId: number) =>
      requestJson<unknown>(
        `/api/admin/locations/${selectedLocationId}/webcams/${cameraId}`,
        { method: "DELETE" },
      ),
    onSuccess: async () => {
      setNotice("Review reset. Windy proximity defaults are restored.");
      setConfirmedSpotView(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["/api/admin/locations", selectedLocationId, "webcams"] }),
        queryClient.invalidateQueries({ queryKey: ["/api/locations", selectedLocationId, "webcams"] }),
      ]);
    },
    onError: (error: Error) => setNotice(error.message),
  });

  useEffect(() => {
    if (!locationId && locations.length) setLocationId(String(locations[0].id));
  }, [locationId, locations]);

  useEffect(() => {
    if (!candidates.length) {
      setCandidateId(null);
      return;
    }
    if (candidateId === null || !candidates.some((camera) => camera.id === candidateId)) {
      const next = candidates.find((camera) => reviews.some((review) => review.cameraId === camera.id)) ?? candidates[0];
      setCandidateId(next.id);
    }
  }, [candidateId, candidates, reviews]);

  useEffect(() => {
    if (selectedReview) setRank(String(selectedReview.sortOrder));
    else setRank("0");
    setConfirmedSpotView(false);
    setNotice("");
  }, [locationId, candidateId, selectedCandidate?.reviewToken, selectedReview?.sortOrder, selectedReview?.status]);

  const parsedRank = Number(rank);
  const validRank = Number.isInteger(parsedRank) && parsedRank >= 0 && parsedRank <= 999;
  const busy = reviewMutation.isPending || resetMutation.isPending;
  const unavailableReviews = reviews.filter((review) => !candidates.some((camera) => camera.id === review.cameraId));

  function save(status: ReviewStatus, camera: WebcamCandidate) {
    if (status === "approved" && !confirmedSpotView) return;
    if (!validRank) return;
    setNotice("");
    reviewMutation.mutate({ cameraId: camera.id, status, sortOrder: parsedRank });
  }

  return (
    <section className="space-y-6" aria-labelledby="webcam-admin-title">
      <header className="rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-7">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              <ShieldCheck className="h-4 w-4" /> Editorial review
            </p>
            <h2 id="webcam-admin-title" className="text-2xl font-semibold tracking-tight">Windy camera approvals</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
              Review the actual snapshot before associating a camera with a named surf break. Distance alone is never evidence of a spot view.
            </p>
          </div>
          <div className="w-full md:w-80">
            <Label htmlFor="webcam-location">Surf spot</Label>
            {locationsQuery.isError ? (
              <div className="mt-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                Could not load locations. <button className="underline" onClick={() => void locationsQuery.refetch()}>Retry</button>
              </div>
            ) : (
              <select
                id="webcam-location"
                value={locationId}
                onChange={(event) => {
                  setLocationId(event.target.value);
                  setCandidateId(null);
                  setNotice("");
                }}
                disabled={busy || locationsQuery.isPending || locations.length === 0}
                className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {locationsQuery.isPending && <option value="">Loading spots…</option>}
                {!locationsQuery.isPending && !locations.length && <option value="">No locations found</option>}
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>{location.name} · {location.city}, {location.country}</option>
                ))}
              </select>
            )}
          </div>
        </div>
        <div className="mt-5 grid gap-3 rounded-xl bg-muted/60 p-4 text-sm leading-6 text-muted-foreground md:grid-cols-2">
          <p><strong className="text-foreground">Nearby</strong> means the camera is within the search radius; it may show another beach or no surf break at all.</p>
          <p><strong className="text-foreground">Approved</strong> means an administrator visually confirmed the named break in the image. It is not a surf measurement or guarantee.</p>
        </div>
      </header>

      {webcamsQuery.data?.discoveryUnavailable && (
        <p role="alert" className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 text-sm">
          Windy candidates are temporarily unavailable. Saved reviews below are retained and can still be reset.
          {" "}<button className="underline" onClick={() => void webcamsQuery.refetch()}>Retry discovery</button>
        </p>
      )}
      {!selectedLocationId ? null : webcamsQuery.isPending ? (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1.4fr)_minmax(18rem,0.8fr)]" aria-label="Loading Windy cameras">
          <div className="aspect-video animate-pulse rounded-2xl bg-muted" />
          <div className="h-64 animate-pulse rounded-2xl bg-muted" />
        </div>
      ) : webcamsQuery.isError ? (
        <div className="rounded-2xl border border-destructive/25 bg-destructive/5 px-6 py-10 text-center">
          <AlertCircle className="mx-auto h-7 w-7 text-destructive" />
          <h3 className="mt-3 font-semibold">Camera candidates could not be loaded</h3>
          <p className="mt-1 text-sm text-muted-foreground">{(webcamsQuery.error as Error).message}</p>
          <Button className="mt-4" variant="outline" onClick={() => void webcamsQuery.refetch()}>Retry</Button>
        </div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1.4fr)_minmax(18rem,0.8fr)]">
          <Card className="overflow-hidden">
            {selectedCandidate ? (
              <>
                <div className="relative aspect-video bg-muted">
                  <a href={selectedCandidate.detailUrl} target="_blank" rel="noopener noreferrer" aria-label={`Open ${selectedCandidate.title} on Windy`} className="absolute inset-0 flex items-center justify-center">
                    <img src={selectedCandidate.previewUrl} alt={`Windy snapshot: ${selectedCandidate.title}`} className="max-h-full max-w-full object-contain" />
                  </a>
                  <span className="absolute bottom-3 left-3 rounded-full bg-background/90 px-3 py-1 text-xs font-medium shadow">
                    {selectedCandidate.distanceKm.toFixed(1)} km from spot
                  </span>
                </div>
                <CardContent className="space-y-5 p-5">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Windy camera</p>
                      <h3 className="mt-1 text-lg font-semibold">{selectedCandidate.title}</h3>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Coordinates {selectedCandidate.latitude.toFixed(4)}, {selectedCandidate.longitude.toFixed(4)}
                        {selectedCandidate.beachReviewed ? " · beach candidate" : " · beach not pre-classified"}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <a href={selectedCandidate.previewUrl} target="_blank" rel="noopener noreferrer" className="inline-flex h-9 items-center gap-2 rounded-md border border-input px-3 text-xs font-semibold hover:bg-muted">
                        Snapshot <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                      <a href={selectedCandidate.detailUrl} target="_blank" rel="noopener noreferrer" className="inline-flex h-9 items-center gap-2 rounded-md border border-input px-3 text-xs font-semibold hover:bg-muted">
                        Windy page <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </div>
                  </div>

                  <div className="grid gap-4 rounded-xl border bg-muted/30 p-4 sm:grid-cols-[10rem_1fr] sm:items-end">
                    <div>
                      <Label htmlFor="camera-rank">Display rank</Label>
                      <Input id="camera-rank" type="number" min={0} max={999} step={1} value={rank} onChange={(event) => setRank(event.target.value)} className="mt-2" />
                      <p className="mt-1 text-xs text-muted-foreground">0–999 · lower ranks first</p>
                    </div>
                    <div className="space-y-3">
                      <label className="flex cursor-pointer items-start gap-3 text-sm leading-5">
                        <input
                          type="checkbox"
                          checked={confirmedSpotView}
                          onChange={(event) => setConfirmedSpotView(event.target.checked)}
                          className="mt-1 h-4 w-4 accent-primary"
                          aria-label={`Confirm ${selectedCandidate.title} visibly shows the named break`}
                        />
                        <span>I reviewed this snapshot and can actually see the named break here. Proximity or a beach label is not enough.</span>
                      </label>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          disabled={busy || !validRank || !confirmedSpotView}
                          onClick={() => save("approved", selectedCandidate)}
                        >
                          <Check className="mr-2 h-4 w-4" /> Approve spot view
                        </Button>
                        <Button variant="outline" disabled={busy || !validRank} onClick={() => save("excluded", selectedCandidate)}>
                          Exclude
                        </Button>
                        {selectedReview && (
                          <Button variant="ghost" disabled={busy} onClick={() => resetMutation.mutate(selectedCandidate.id)}>
                            <RotateCcw className="mr-2 h-4 w-4" /> Reset
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  {!validRank && <p className="text-sm text-destructive">Rank must be a whole number from 0 to 999.</p>}
                  {selectedReview && (
                    <p className="text-xs text-muted-foreground">
                      Saved as <strong className="capitalize text-foreground">{selectedReview.status}</strong>
                      {selectedReview.status === "approved" ? ` · rank ${selectedReview.sortOrder}` : ""}
                      {" · "}reviewed {prettyDate(selectedReview.reviewedAt)}
                      {selectedReview.status === "approved" &&
                        (selectedReview.title !== selectedCandidate.title ||
                        selectedReview.latitude !== selectedCandidate.latitude ||
                        selectedReview.longitude !== selectedCandidate.longitude) &&
                        " · Identity changed: approval is not applied. Review again."}
                    </p>
                  )}
                </CardContent>
              </>
            ) : (
              <div className="flex min-h-72 flex-col items-center justify-center p-8 text-center">
                <MapPin className="h-8 w-8 text-muted-foreground" />
                <h3 className="mt-3 font-semibold">No Windy candidates for this spot</h3>
                <p className="mt-1 max-w-md text-sm text-muted-foreground">There are no snapshots available in the configured search radius right now. Saved decisions remain listed below.</p>
              </div>
            )}
          </Card>

          <aside className="space-y-5">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Candidates <span className="ml-1 text-sm font-normal text-muted-foreground">({candidates.length})</span></CardTitle>
                <p className="text-xs text-muted-foreground">
                  {webcamsQuery.data?.radiusKm} km search radius
                  {webcamsQuery.data?.fetchedAt ? ` · fetched ${prettyDate(webcamsQuery.data.fetchedAt)}` : ""}
                </p>
              </CardHeader>
              <CardContent className="space-y-2">
                {candidates.length ? candidates.map((camera) => {
                  const review = reviews.find((item) => item.cameraId === camera.id);
                  const active = camera.id === candidateId;
                  return (
                    <button
                      type="button"
                      disabled={busy}
                      key={camera.id}
                      onClick={() => setCandidateId(camera.id)}
                      className={`flex w-full items-center justify-between gap-3 rounded-xl border p-3 text-left transition ${active ? "border-primary/50 bg-primary/[0.06]" : "border-border hover:bg-muted/60"}`}
                      aria-pressed={active}
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium">{camera.title}</span>
                        <span className="mt-1 block text-xs text-muted-foreground">{camera.distanceKm.toFixed(1)} km away</span>
                      </span>
                      <span className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-semibold capitalize ${review?.status === "approved" ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : review?.status === "excluded" ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground"}`}>
                        {review?.status === "approved" && (review.title !== camera.title ||
                          review.latitude !== camera.latitude || review.longitude !== camera.longitude)
                          ? "Needs re-review" : review?.status ?? "nearby"}
                      </span>
                    </button>
                  );
                }) : <p className="rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground">No candidate snapshots returned.</p>}
              </CardContent>
            </Card>
          </aside>
        </div>
      )}

      {unavailableReviews.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Saved reviews · currently unavailable</CardTitle>
            <p className="text-sm text-muted-foreground">Saved exclusions and approvals stay visible even when Windy no longer returns that camera, so each decision can be reversed.</p>
          </CardHeader>
          <CardContent className="space-y-2">
            {unavailableReviews.map((review) => (
              <div key={review.cameraId} className="flex flex-col gap-3 rounded-xl border p-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-medium">{review.title}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    <span className="capitalize">{review.status}</span>
                    {review.status === "approved" ? ` · rank ${review.sortOrder}` : ""}
                    {" · "}coordinates {review.latitude.toFixed(4)}, {review.longitude.toFixed(4)}
                    {" · "}reviewed {prettyDate(review.reviewedAt)}
                  </p>
                </div>
                <Button variant="outline" size="sm" disabled={busy} onClick={() => resetMutation.mutate(review.cameraId)}>
                  <RotateCcw className="mr-2 h-4 w-4" /> Reset review
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {notice && (
        <p role="status" className={`rounded-lg border px-4 py-3 text-sm ${reviewMutation.isError || resetMutation.isError ? "border-destructive/30 bg-destructive/5 text-destructive" : "border-primary/20 bg-primary/[0.04] text-foreground"}`}>
          {notice}
        </p>
      )}
      <p className="border-t pt-4 text-xs leading-5 text-muted-foreground">
        Windy camera imagery and timelapses remain freely accessible and attributed to Windy. Approval only identifies a visually reviewed spot view; it does not validate surf conditions or measurements.
      </p>
    </section>
  );
}
